import { z } from 'zod';
import { IdSchema } from './common.schema';

// Integrator (release 2026-08-26). Feature de embeds federados: pestañas
// dentro del PIM que renderizan el front del partner (VTEX/Carulla/Rappi/…)
// vía iframe. El handshake auth es un ticket single-use opaco emitido por
// el PIM; el back del integrador lo redime contra el PIM usando su API key
// existente. Sin JWT firmado, sin JWKs — se apoya en el canal autenticado
// que ya compartimos con cada B2B client.
//
// Ver docs de peer: `docs/plans/RETOMAR-perf-alloydb.md` (spec frozen).

// Read shape user-facing (`GET /integrators`) — solo los 4 fields que el
// sidebar necesita. Sin leak de scopes/roleIds. Fail-closed: user sin role
// asignado a un integrador NO lo ve.
export const IntegratorPublicSchema = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  iconUrl: z.string().url().nullable().optional(),
  embedUrl: z.string().url(),
});
export type IntegratorPublic = z.infer<typeof IntegratorPublicSchema>;

export const IntegratorPublicListSchema = z.array(IntegratorPublicSchema);
export type IntegratorPublicList = z.infer<typeof IntegratorPublicListSchema>;

// Read shape admin (`GET /admin/integrators`). Incluye rows inactivas y los
// campos de configuración (roleIds + grantedScopes). Gate `integrations:manage`.
export const IntegratorAdminSchema = z.object({
  id: IdSchema,
  slug: z.string().min(1),
  name: z.string().min(1),
  iconUrl: z.string().url().nullable().optional(),
  embedUrl: z.string().url(),
  // Scopes que viajan en el ticket verify response — el back del integrador
  // los usa para gate per-endpoint. El PIM NO los interpreta.
  grantedScopes: z.array(z.string().min(1)).default([]),
  // Roles con acceso a este integrador. Join table `integrator_role`.
  // `PATCH` reemplaza el set completo (no merge).
  roleIds: z.array(IdSchema).default([]),
  isActive: z.boolean(),
  order: z.number().int().nonnegative(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
});
export type IntegratorAdmin = z.infer<typeof IntegratorAdminSchema>;
// Peer confirmó (2026-08-26): el endpoint NO paginated. Devuelve el shape
// global `{data:[...], error, meta}`; el interceptor `unwrapEnvelope` del
// front resuelve `data` a un array plano cuando `meta.pagination` está
// ausente. Consumir como array directo.
export const IntegratorAdminListSchema = z.array(IntegratorAdminSchema);
export type IntegratorAdminList = z.infer<typeof IntegratorAdminListSchema>;

// POST /admin/integrators body. `slug` debe coincidir con el slug del B2B
// client (constraint del back: el ticket se ata al cliente que puede
// redimirlo). Validar contra la lista de clientes en el form antes de
// mandar — evita el 409 typo del slug.
export const IntegratorCreateSchema = z.object({
  slug: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9-]+$/, 'Solo minúsculas, números y guiones'),
  name: z.string().min(1).max(120),
  iconUrl: z.string().url().max(500).optional(),
  embedUrl: z
    .string()
    .url()
    .max(500)
    .refine((v) => v.startsWith('https://'), 'Debe ser HTTPS'),
  grantedScopes: z.array(z.string().min(1).max(64)).max(100).default([]),
  roleIds: z.array(IdSchema).default([]),
  order: z.number().int().nonnegative().default(0),
});
export type IntegratorCreate = z.infer<typeof IntegratorCreateSchema>;

// PATCH /admin/integrators/:id. `slug` NO editable. `roleIds` y
// `grantedScopes` REEMPLAZAN el set completo cuando la key está presente;
// omitirla deja el valor actual intacto.
export const IntegratorUpdateSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  iconUrl: z.string().url().max(500).nullable().optional(),
  embedUrl: z
    .string()
    .url()
    .max(500)
    .refine((v) => v.startsWith('https://'), 'Debe ser HTTPS')
    .optional(),
  grantedScopes: z.array(z.string().min(1).max(64)).max(100).optional(),
  roleIds: z.array(IdSchema).optional(),
  order: z.number().int().nonnegative().optional(),
  isActive: z.boolean().optional(),
});
export type IntegratorUpdate = z.infer<typeof IntegratorUpdateSchema>;

// POST /integrators/:slug/session. Response del ticket. El front:
//   - lo recibe en el `IntegratorFrame` al mount.
//   - lo pasa al iframe vía `postMessage` (targetOrigin pinneado al origin
//     del integrador). NUNCA en la URL.
//   - lo descarta del state after send — TTL 2min, single-use.
export const IntegratorSessionSchema = z.object({
  ticket: z.string().min(1),
  expiresAt: z.coerce.date(),
});
export type IntegratorSession = z.infer<typeof IntegratorSessionSchema>;
